import React, { useState, useEffect, useCallback } from "react";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import MuiButton from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Box from "@material-ui/core/Box";
import CloseIcon from "@material-ui/icons/Close";

// Dialog Component
const CSDialog = ({
  isOpen,
  title,
  placeholder,
  confirmText,
  cancelText = "Cancel",
  onSubmit,
  onCancel,
  mode = "inputvalue", // default for backward compatibility
  initialValue = "",
  initialDescription = "",
  message = null,
}) => {
  const [name, setName] = useState("");
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (isOpen) {
      setName(initialValue);
      setFile(null);
      setDescription(initialDescription);
    }
  }, [isOpen]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0] || null);
    if (e.target.files[0] && !name) {
      setName(e.target.files[0].name);
    }
  };

  const handleConfirm = () => {
    if (mode === "inputvalue") {
      onSubmit(name);
    } else if (mode === "confirmation") {
      onSubmit();
    } else {
      onSubmit({ name, file, description });
    }
  };

  const isConfirmDisabled =
    (mode === "inputvalue" && !name) ||
    (mode === "upload" && !file);

  return (
    <Dialog open={!!isOpen} onClose={onCancel} aria-labelledby="csdialog-title">
      <DialogTitle id="csdialog-title" disableTypography style={{ backgroundColor: "#f0f0f0", padding: "8px 24px" }}>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1} style={{ fontSize: "1.25rem", fontWeight: 500 }}>{title}</Box>
          <IconButton onClick={onCancel} aria-label="close">
            <CloseIcon style={{ fontSize: "2rem" }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 24, paddingBottom: 24 }}>
        {mode === "inputvalue" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 320 }}>
            <label htmlFor="csdialog-name">{placeholder}</label>
            <input
              id="csdialog-name"
              type="text"
              className="form-control"
              value={name}
              placeholder={placeholder}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
        )}
        {mode === "upload" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 320 }}>
            <div>
              <label htmlFor="csdialog-desc">Description</label>
              <textarea
                id="csdialog-desc"
                className="form-control"
                value={description}
                placeholder="Enter description"
                onChange={(e) => setDescription(e.target.value)}
                rows={3}
              />
            </div>
            <div>
              <label htmlFor="csdialog-file">Select file</label>
              <input
                id="csdialog-file"
                type="file"
                className="form-control"
                onChange={handleFileChange}
              />
            </div>
          </div>
        )}
        {mode === "confirmation" && message && (
          <div style={{ minWidth: 320 }}>{message}</div>
        )}
      </DialogContent>
      <DialogActions style={{ paddingBottom: 24 }}>
        <MuiButton
          onClick={onCancel}
          style={{ background: "#fff", lineHeight: "15px", fontSize: "15px" }}
        >
          {cancelText}
        </MuiButton>
        <MuiButton
          variant="contained"
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          style={{ background: "#0070C0", color: "#fff", lineHeight: "15px", fontSize: "15px" }}
        >
          {confirmText}
        </MuiButton>
      </DialogActions>
    </Dialog>
  );
};

// Custom hook to show CSDialog and get result as a promise
export function useCSDialog() {
  const [dialogProps, setDialogProps] = useState(null);

  const showDialog = useCallback(
    (options) =>
      new Promise((resolve) => {
        setDialogProps({
          ...options,
          isOpen: true,
          onSubmit: (value) => {
            setDialogProps(null);
            resolve(value ?? true);
          },
          onCancel: () => {
            setDialogProps(null);
            resolve(null);
          },
        });
      }),
    []
  );

  const dialog = dialogProps ? <CSDialog {...dialogProps} /> : null;

  return [showDialog, dialog];
}

export default CSDialog;